import i18n from '../i18n';
import { _dataProvider } from '../dataProvider/DataProvider';

import type { IGroupDetailData, IGroupItem } from '@/dataProvider/IGroups';

export type IMessageData<T = any> = {
  type:
    | 'Request-MeetingBreakoutRoomParams'
    | 'Response-MeetingBreakoutRoomParams'
    | 'Received-MeetingBreakoutRoomParams';
  payload?: T;
};

const getDebug = () => {
  return {
    isDebug: new URLSearchParams(location.search).has('debug'),
    // debugMeetingOrigin: 'https://local.fishmeet.online:3000'
    debugMeetingOrigin: 'https://127.0.0.1:8080'
  };
};

const getMeetingWindowFeatures = () =>
  [
    'height=' + screen.height,
    'width=' + screen.width,
    'fullscreen=yes', // only works in IE, but here for completeness
    'toolbar=0,location=0,menubar=0,status=0,location=0,titlebar=0,top=100,left=0'
  ].join(',');

type IMeetingBaseParams = {
  groupId: IGroupItem['groupId'];
};

type IOpenMeetingParams = IMeetingBaseParams & {
  enablePreBreakout?: boolean;
};
export const getMeetingUrl = async (params: IOpenMeetingParams) => {
  const { groupId, enablePreBreakout } = params;

  try {
    let { url } = (await _dataProvider.getGroupMeetLink(groupId)) ?? {};
    if (!url) {
      return undefined;
    }

    const { origin, pathname, search, hash } = new URL(url);
    const iUsp = new URLSearchParams(search);
    if (enablePreBreakout) {
      iUsp.set('pre-breakout', '1');
    }

    if (i18n.language === 'eng') {
      iUsp.set('zhCN', 'en');
    }

    url = `${origin}${pathname}?${iUsp.toString()}${hash}`;

    const features = getMeetingWindowFeatures();

    const { isDebug, debugMeetingOrigin } = getDebug();
    if (isDebug) {
      url = `${debugMeetingOrigin}${pathname}?${iUsp.toString()}${hash}`;
    }

    console.log('[Meeting] getUrl: ', { url, features });
    return {
      url,
      features
    };
  } catch (err) {
    console.log('getMeetingUrl error:', err);
  }
};

export const openMeeting = async (params: IOpenMeetingParams) => {
  const { groupId, enablePreBreakout } = params;

  // Must run synchronously in the click gesture, before any await (iOS Safari blocks later window.open calls)
  prepareMeetingWindow();

  // if meeting window is existed and opened, focus it
  if (existMeetingWindow()) {
    if (_meetingGroupId === groupId) {
      _meetingWindow!.focus();
      return;
    }

    const cont = confirm('你正在一个会议中，你要退出，加入一个新会议吗?');

    if (!cont) {
      cancelPendingMeetingWindow();
      _meetingWindow!.focus();
      return;
    }
  }

  const { url, features = '' } =
    (await getMeetingUrl({
      groupId,
      enablePreBreakout
    })) ?? {};

  if (!url) {
    cancelPendingMeetingWindow();
    alert('Network connection failed, try again');
    return;
  }

  openMeetingWindow({ url, features, groupId });
};

let _meetingWindow: Window | null;
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
let _meetingGroupId: IGroupItem['groupId'] | null;

type IOpenMeetingWindowParams = {
  url: string;
  features: string;
  groupId: IGroupItem['groupId'];
};

type IExistMeetingWindow = Pick<IOpenMeetingWindowParams, 'groupId'>;
export const existMeetingWindow = (params?: IExistMeetingWindow) => {
  const { groupId } = params ?? {};
  if (groupId) {
    _meetingWindow && !_meetingWindow.closed && _meetingGroupId === groupId;
  }

  return _meetingWindow && !_meetingWindow.closed;
};

// A blank window opened synchronously in the user's click, navigated to the meeting url once it is known.
let _pendingMeetingWindow: Window | null = null;

export const prepareMeetingWindow = () => {
  if (existMeetingWindow() || (_pendingMeetingWindow && !_pendingMeetingWindow.closed)) {
    return;
  }

  try {
    _pendingMeetingWindow = window.open('', 'idigestMeeting', getMeetingWindowFeatures());
  } catch (err) {
    console.log('[Meeting] prepareMeetingWindow error:', err);
    _pendingMeetingWindow = null;
  }
};

export const cancelPendingMeetingWindow = () => {
  if (_pendingMeetingWindow && !_pendingMeetingWindow.closed) {
    _pendingMeetingWindow.close();
  }
  _pendingMeetingWindow = null;
};

export const openMeetingWindow = (params: IOpenMeetingWindowParams) => {
  const { url, features = '', groupId } = params ?? {};

  if (_pendingMeetingWindow && !_pendingMeetingWindow.closed) {
    _meetingWindow = _pendingMeetingWindow;
    _pendingMeetingWindow = null;
    _meetingWindow.location.href = url;
  } else {
    _pendingMeetingWindow = null;
    _meetingWindow = window.open(url, 'idigestMeeting', features);
  }

  if (!_meetingWindow) {
    // Popup blocked: fall back to joining in the current tab
    window.location.assign(url);
    return null;
  }

  _meetingWindow.focus();
  _meetingGroupId = groupId;

  return _meetingWindow;
};

type ISendBreakoutConfig = IMeetingBaseParams & {
  onSuccess?: () => void;
};
export const sendBreakoutConfig = (params: ISendBreakoutConfig) => {
  const { groupId, onSuccess } = params;

  let messageListener: ((event: MessageEvent<IMessageData>) => void) | undefined = async (
    event: MessageEvent<IMessageData>
  ) => {
    const { type: msgType } = event.data ?? {};

    if (msgType === 'Request-MeetingBreakoutRoomParams') {
      try {
        const { meetingCode, meetingData } = ((await _dataProvider.getGroupInfo(groupId)) as IGroupDetailData) ?? {};
        event.source?.postMessage(
          {
            type: 'Response-MeetingBreakoutRoomParams',
            payload: { groupId, meetingCode, meetingData }
          },
          { targetOrigin: event.origin }
        );
      } catch (err) {
        console.log('[sendBreakoutConfig] getMeetingData error:', err);
        return;
      }

      console.log('IDigest: Response-MeetingBreakoutRoomParams', event.data);
    }

    if (msgType === 'Received-MeetingBreakoutRoomParams') {
      clean();
      onSuccess?.();
      console.log('IDigest: Received-MeetingBreakoutRoomParams');
    }
  };

  window.addEventListener('message', messageListener);

  const clean = () => {
    if (!messageListener) {
      return;
    }
    window.removeEventListener('message', messageListener);
    messageListener = undefined;
  };

  return { clean };
};
